/**
 * storage-test.js  — DIAGNOSTIC, TEMPORARY
 * ----------------------------------------------------------------------------
 * Drives the hidden /pages/storage-test page (sections/storage-test.liquid)
 * used to investigate the Facebook in-app browser freeze
 * (ClaudeDevPlans/ToDo/FB-INAPP-FREEZE-INVESTIGATION.md).
 *
 * Theory under test: affected customers carry accumulated cookies/localStorage
 * and the webview wedges when storage is at quota (or requests carry bloated
 * cookie headers). This page lets a test phone reach that state in one tap
 * instead of weeks of browsing.
 *
 * Safety: writes ONLY keys prefixed "storage-test:" and cookies prefixed
 * "st_test_" (Max-Age 24h, so they self-expire even if the phone gets stuck).
 * "Clear test data" removes exactly those. Nothing else is touched, nothing
 * leaves the device. Delete this file + section + template when the bug is
 * closed.
 * ----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-storage-test]');
  if (!root) return;

  var logEl = root.querySelector('[data-st-log]');
  var CHUNK = new Array(100 * 1024).join('x'); // ~100k chars (~200KB as UTF-16)
  var MAX_CHUNKS = 600;                        // hard safety cap (~120MB)
  var COOKIE_VAL = new Array(3500).join('c');  // ~3.4KB per cookie

  function log(msg) {
    var time = new Date().toTimeString().slice(0, 8);
    logEl.textContent += '[' + time + '] ' + msg + '\n';
    logEl.scrollTop = logEl.scrollHeight;
  }

  function localStorageBytes() {
    var total = 0;
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      total += (k.length + (localStorage.getItem(k) || '').length) * 2;
    }
    return total;
  }

  function fmt(bytes) {
    if (bytes > 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes > 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  }

  function measure() {
    log('--- Current state ---');
    log('localStorage: ' + localStorage.length + ' keys, ' + fmt(localStorageBytes()));
    log('cookies sent by this browser: ' + fmt(document.cookie.length) +
      ' (' + document.cookie.split(';').filter(function (c) { return c.trim(); }).length + ' cookies)');
    if (navigator.storage && navigator.storage.estimate) {
      navigator.storage.estimate().then(function (est) {
        log('browser storage estimate: using ' + fmt(est.usage || 0) +
          ' of ' + fmt(est.quota || 0) + ' allowed');
      }).catch(function () {
        log('storage estimate not available in this browser');
      });
    } else {
      log('storage estimate not available in this browser');
    }
  }

  function fillLocalStorage() {
    log('Filling localStorage until the browser refuses…');
    var n = 0;
    try {
      // resume numbering after any earlier run
      while (localStorage.getItem('storage-test:' + n) !== null) n++;
      for (; n < MAX_CHUNKS; n++) {
        localStorage.setItem('storage-test:' + n, CHUNK);
      }
      log('Hit the safety cap (' + MAX_CHUNKS + ' chunks) without the browser refusing — quota is large here.');
    } catch (e) {
      log('Browser refused at chunk ' + n + ' (' + (e && e.name) + ') — localStorage is now AT QUOTA.');
    }
    log('localStorage now: ' + fmt(localStorageBytes()));
    log('NOW: tap "Go to home page" below and watch whether the site freezes.');
  }

  function fillCookies(count) {
    log('Writing ' + count + ' bloat cookies (~3.4KB each, expire in 24h)…');
    for (var i = 0; i < count; i++) {
      document.cookie = 'st_test_' + i + '=' + COOKIE_VAL + '; path=/; Max-Age=86400; SameSite=Lax';
    }
    log('Cookie header is now ~' + fmt(document.cookie.length) + '.');
    log('NOW: tap "Go to home page" below. If pages stop loading entirely, come BACK here and press "Clear test data" — if this page will not load either, that stuck state is itself the reproduction (recover via FB app settings > clear browsing data).');
  }

  function listCookies() {
    log('--- Cookie inventory (largest first) ---');
    var items = document.cookie.split(';')
      .map(function (c) {
        var eq = c.indexOf('=');
        return { name: c.slice(0, eq).trim(), size: c.length };
      })
      .filter(function (c) { return c.name; })
      .sort(function (a, b) { return b.size - a.size; });
    items.forEach(function (c) { log('  ' + c.name + ' — ' + fmt(c.size)); });
    log('Total: ' + items.length + ' cookies, ' + fmt(document.cookie.length));
  }

  function listStorageKeys() {
    log('--- localStorage inventory (largest first) ---');
    var items = [];
    for (var i = 0; i < localStorage.length; i++) {
      var k = localStorage.key(i);
      items.push({ name: k, size: (k.length + (localStorage.getItem(k) || '').length) * 2 });
    }
    items.sort(function (a, b) { return b.size - a.size; });
    items.forEach(function (it) { log('  ' + it.name + ' — ' + fmt(it.size)); });
    log('Total: ' + items.length + ' keys, ' + fmt(localStorageBytes()));
  }

  function listExternalHosts() {
    log('--- External hosts this page actually loaded from ---');
    var counts = {};
    performance.getEntriesByType('resource').forEach(function (r) {
      try {
        var h = new URL(r.name).hostname;
        if (h && h !== location.hostname) counts[h] = (counts[h] || 0) + 1;
      } catch (err) { /* ignore unparseable entries */ }
    });
    var hosts = Object.keys(counts).sort();
    if (!hosts.length) log('  (none — everything came from ' + location.hostname + ')');
    hosts.forEach(function (h) { log('  ' + h + ' — ' + counts[h] + ' request(s)'); });
    log('Total external hosts: ' + hosts.length + '. (Anything mida/tidio/clarity/datadome-looking here means that service is STILL ACTIVE.)');
  }

  function clearTestData() {
    var removed = 0;
    for (var i = localStorage.length - 1; i >= 0; i--) {
      var k = localStorage.key(i);
      if (k && k.indexOf('storage-test:') === 0) {
        localStorage.removeItem(k);
        removed++;
      }
    }
    var cookiesCleared = 0;
    document.cookie.split(';').forEach(function (c) {
      var name = c.split('=')[0].trim();
      if (name.indexOf('st_test_') === 0) {
        document.cookie = name + '=; path=/; Max-Age=0';
        cookiesCleared++;
      }
    });
    log('Removed ' + removed + ' test localStorage keys and ' + cookiesCleared + ' test cookies. Site data written by the site itself was left alone.');
  }

  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-st-action]');
    if (!btn) return;
    switch (btn.getAttribute('data-st-action')) {
      case 'measure': measure(); break;
      case 'list-cookies': listCookies(); break;
      case 'list-storage': listStorageKeys(); break;
      case 'list-hosts': listExternalHosts(); break;
      case 'fill-storage': fillLocalStorage(); break;
      case 'fill-cookies-mild': fillCookies(4); break;
      case 'fill-cookies-medium': fillCookies(8); break;
      case 'fill-cookies-heavy': fillCookies(30); break;
      case 'clear': clearTestData(); break;
    }
  });

  log('Storage test ready. Start with "Measure current state".');
  measure();
})();
